import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandS, LabelS } from '#src/atoms/typography/index.tsx';
import { PinButton } from '#src/components/navigation/AppNavigation/UserCurrencies/PinButton.tsx';
import {
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_MARKET_LEGACY_PACKS,
  ANY_SPORT_SERIES_MARKET_COMMON_PACKS,
} from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { usePinnedShardRarity } from '#src/hooks/config/usePinnedShardRarity.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { isSpecialCardSet, isSupportedCardSet } from '#src/lib/cardSets.ts';
import { formatScarcity } from '#src/lib/cards.ts';
import { getRarityColor } from '#src/lib/colors.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

const RarityWrapper = styled(Vertical)`
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit) 0;
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-nd-50);
    }
  }
`;

const RarityContainer = styled(Vertical).attrs({ center: true })`
  width: 100%;
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

type Props = {
  rarity: Rarity;
  cardShardsCount: number;
  cardSet: Nullable<{
    slug: string;
    name: string;
  }>;
  onClick?: () => void;
};

export const ShardRarityItem = ({
  rarity,
  cardShardsCount,
  cardSet,
  onClick,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isLaptop = useIsLaptop();
  const { pinnedShardRarity, pinnedShardCardSet, updatePinnedShardRarity } =
    usePinnedShardRarity();

  const isPinned =
    pinnedShardRarity === rarity &&
    ((!pinnedShardCardSet && !cardSet?.slug) ||
      pinnedShardCardSet === cardSet?.slug);

  const cardSetSlug = isSupportedCardSet(cardSet?.slug)
    ? cardSet?.slug
    : undefined;
  const isSpecialSet = isSpecialCardSet(cardSet?.slug);

  return (
    <RarityContainer>
      {!!isLaptop && (
        <PinButton
          onClick={() => updatePinnedShardRarity(rarity, cardSetSlug)}
          isPinned={isPinned}
        />
      )}
      <RarityWrapper
        as={Link}
        to={
          rarity === Rarity.common
            ? generateSportContextPath(
                isSpecialSet
                  ? ANY_SPORT_SERIES_MARKET_COMMON_PACKS
                  : ANY_SPORT_MARKET_LEGACY_PACKS
              )
            : generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
                params: { type: rarity },
              })
        }
        onClick={onClick}
      >
        <LabelS
          color={
            isSpecialSet
              ? getRarityColor(rarity, { cardSet: cardSetSlug })
              : 'var(--c-nd-600)'
          }
        >
          {isSpecialSet ? cardSet?.name : formatScarcity(rarity)}
        </LabelS>
        <BrandS>
          <FormattedNumber value={cardShardsCount} />
        </BrandS>
      </RarityWrapper>
      {!isLaptop && (
        <PinButton
          onClick={() => updatePinnedShardRarity(rarity, cardSetSlug)}
          isPinned={isPinned}
        />
      )}
    </RarityContainer>
  );
};
